import { getNonEmptyArrayLastElement, isArrayNotEmpty } from '../../../utils'

import { Capability, ConnectwareError, ConnectwareErrorType, type Topic, type TopicsChunk, Translation } from '../../../domain'

import type { TopicsService, TranslationService } from '../../../application'

import type { BackendDefinition } from '../../Connectware'
import { type ConnectwareHTTPServiceOptions, FetchConnectwareHTTPService } from '../Base'

type Children = BackendDefinition<'topic-explorer', 'LevelsData'>['children']

export class ConnectwareHTTPTopicsService extends FetchConnectwareHTTPService implements Pick<TopicsService, 'fetchTopicsChunk'> {
    /**
     * This limit is here because we **may** have situations where thousands of children are added at once
     * This could cause issues when rendering all at once
     * So for now, they are loaded in chunks
     */
    protected readonly maxChildrenPerTopic: number = 100

    private static mapTopics (childrenSlice: Children, prefix: Topic['fullPath']): TopicsChunk['topics'] {
        return childrenSlice.map(({ has_more: hasChildren, name }) => ({
            hasChildren,
            fullPath: prefix ? [prefix, name].join('/') : name,
            name,
        }))
    }

    constructor (options: ConnectwareHTTPServiceOptions, private readonly translationService: TranslationService) {
        super(options)
    }

    private mapNextChunk (children: Children, childrenSlice: Children): TopicsChunk['nextChunk'] {
        const isSliceNotAtLastChunk =
            isArrayNotEmpty(children) && isArrayNotEmpty(childrenSlice) && getNonEmptyArrayLastElement(childrenSlice) !== getNonEmptyArrayLastElement(children)

        if (isSliceNotAtLastChunk) {
            return { reference: getNonEmptyArrayLastElement(childrenSlice).name, pageSize: this.maxChildrenPerTopic }
        }

        return null
    }

    fetchTopicsChunk (prefix: Topic['fullPath'], reference: string | null): Promise<TopicsChunk> {
        return this.request<'/api/v2/mqtt/topics/levels', 'GET', TopicsChunk>({
            capability: Capability.TOPIC_CHUNKS_READ,
            path: '/api/v2/mqtt/topics/levels',
            method: 'GET',
            authenticate: true,
            queryParams: prefix ? { prefix } : {},
            handlers: {
                200: async (response) => {
                    const { data } = await response.getJson()

                    const start = reference ? data.children.findIndex((c) => c.name === reference) + 1 : 0
                    const end = start + this.maxChildrenPerTopic

                    const childrenSlice = data.children.slice(start, end)

                    return {
                        topics: ConnectwareHTTPTopicsService.mapTopics(childrenSlice, prefix),
                        nextChunk: this.mapNextChunk(data.children, childrenSlice),
                    }
                },
                500: () => new ConnectwareError(ConnectwareErrorType.SERVER_ERROR, this.translationService.translate(Translation.SERVER_ERROR), { prefix }),
                503: () =>
                    new ConnectwareError(ConnectwareErrorType.SERVER_NOT_AVAILABLE, this.translationService.translate(Translation.SERVER_NOT_AVAILABLE), {
                        prefix,
                    }),
            },
        })
    }
}
